/**
 * Phase 6 — Lead-actor surfaces.
 * Used inside LeadCrewManagement (timesheets + helper-rating panels).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";


export function LeadTimesheetsPanel({ bookingId, invitations, onChange }) {
  const [busy, setBusy] = useState(false);
  const helperInvs = (invitations || []).filter((i) =>
    ["accepted", "withdrawn"].includes(i.state) || i.work_status !== "pending"
  );

  return (
    <div className="avcp-card p-5 mt-4" data-testid={TID.leadTimesheetsTab}>
      <h3 className="font-semibold mb-2">Timesheets</h3>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-4">
        Submit your own hours and approve/reject each Helper Technician&apos;s timesheet.
        Once every timesheet is approved and the Lead&apos;s timesheet is submitted,
        the Client is asked to confirm completion.
      </p>

      <LeadOwnTimesheetForm bookingId={bookingId} onSaved={onChange} disabled={busy} />

      <div className="mt-5">
        <h4 className="font-medium text-sm mb-2">Helper Technician timesheets</h4>
        {helperInvs.length === 0 && (
          <p className="text-xs text-[var(--avcp-ink-soft)]">
            No helpers on this crew yet.
          </p>
        )}
        <ul className="space-y-2">
          {helperInvs.map((inv) => (
            <HelperTimesheetRow
              key={inv.id}
              bookingId={bookingId}
              invitation={inv}
              onAfter={() => { setBusy(false); onChange && onChange(); }}
              onBefore={() => setBusy(true)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}


function LeadOwnTimesheetForm({ bookingId, onSaved, disabled }) {
  const [outcome, setOutcome] = useState("worked");
  const [hours, setHours] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!hours) { toast.error("Hours worked is required"); return; }
    setSubmitting(true);
    try {
      await api(`/leads/me/bookings/${bookingId}/timesheet`, {
        method: "POST",
        body: {
          outcome,
          hours_worked: parseFloat(hours),
          work_started_at: startAt ? new Date(startAt).toISOString() : undefined,
          work_ended_at: endAt ? new Date(endAt).toISOString() : undefined,
          notes: notes || undefined,
        },
      });
      toast.success("Your timesheet was submitted and auto-approved.");
      setHours(""); setStartAt(""); setEndAt(""); setNotes("");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="border border-[var(--avcp-line)] rounded p-3">
      <h4 className="font-medium text-sm mb-2">Submit your hours (Lead Technician)</h4>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-sm">
          Outcome
          <select className="avcp-input w-full" value={outcome}
                  data-testid={TID.leadTimesheetOutcome}
                  onChange={(e) => setOutcome(e.target.value)}>
            <option value="worked">Worked</option>
            <option value="walked_off">Walked off</option>
          </select>
        </label>
        <label className="text-sm">
          Hours worked
          <input type="number" step="0.25" min="0" className="avcp-input w-full"
                  data-testid={TID.leadTimesheetHours}
                  value={hours} onChange={(e) => setHours(e.target.value)} />
        </label>
        <label className="text-sm">
          Work started
          <input type="datetime-local" className="avcp-input w-full"
                  data-testid={TID.leadTimesheetStart}
                  value={startAt} onChange={(e) => setStartAt(e.target.value)} />
        </label>
        <label className="text-sm">
          Work ended
          <input type="datetime-local" className="avcp-input w-full"
                  data-testid={TID.leadTimesheetEnd}
                  value={endAt} onChange={(e) => setEndAt(e.target.value)} />
        </label>
      </div>
      <label className="block text-sm mt-2">
        Notes (optional, private to you + Admin)
        <textarea className="avcp-input w-full" rows={2}
                   data-testid={TID.leadTimesheetNotes}
                   value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div className="flex justify-end mt-3">
        <button className="avcp-btn-primary" disabled={submitting || disabled}
                data-testid={TID.leadTimesheetSubmit}
                onClick={submit}>
          {submitting ? "Submitting…" : "Submit my hours"}
        </button>
      </div>
    </div>
  );
}


function HelperTimesheetRow({ bookingId, invitation, onAfter, onBefore }) {
  const [, setEntries] = useState([]);
  const [, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api(
        `/admin/timesheets?booking_id=${bookingId}&party_kind=helper`
      ).catch(() => null);
      setEntries(r?.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invitation.id]);

  const ws = invitation.work_status;
  const needsApproval = ws === "active";

  const approveLatest = async () => {
    const entryId = window.prompt(
      "Enter the helper's timesheet entry id (visible in their inbox; ask the helper or check audit).",
    );
    if (!entryId) return;
    onBefore && onBefore();
    try {
      await api(`/leads/me/bookings/${bookingId}/timesheets/${entryId.trim()}/approve`,
                  { method: "POST", body: {} });
      toast.success("Helper timesheet approved.");
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { onAfter && onAfter(); }
  };

  const rejectLatest = async () => {
    const entryId = window.prompt("Enter the helper's timesheet entry id to reject.");
    if (!entryId) return;
    const reason = window.prompt("Reason for rejection?");
    if (!reason) return;
    onBefore && onBefore();
    try {
      await api(`/leads/me/bookings/${bookingId}/timesheets/${entryId.trim()}/reject`,
                  { method: "POST", body: { reason } });
      toast.success("Helper timesheet rejected.");
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { onAfter && onAfter(); }
  };

  return (
    <li className="border border-[var(--avcp-line)] rounded p-3 flex items-center gap-3 flex-wrap"
        data-testid={TID.leadTimesheetRow}>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{invitation.helper_display_name}</div>
        <div className="text-xs text-[var(--avcp-ink-soft)] mt-1">
          <span className="avcp-pill">{invitation.state}</span>
          <span className="ml-2 avcp-pill">work: {ws || "pending"}</span>
          {invitation.hours_worked != null && (
            <span className="ml-2">· {invitation.hours_worked}h</span>
          )}
        </div>
      </div>
      {needsApproval && (
        <div className="flex gap-2">
          <button className="avcp-btn-primary text-xs"
                  data-testid={TID.leadTimesheetApprove}
                  onClick={approveLatest}>Approve</button>
          <button className="avcp-btn-secondary text-xs"
                  data-testid={TID.leadTimesheetReject}
                  onClick={rejectLatest}>Reject</button>
        </div>
      )}
    </li>
  );
}


export function LeadRateHelpersPanel({ bookingId, invitations }) {
  const ratable = (invitations || []).filter((i) =>
    ["completed", "walked_off", "did_not_show"].includes(i.work_status)
  );
  if (ratable.length === 0) return null;
  return (
    <div className="avcp-card p-5 mt-4" data-testid={TID.leadRatingsPanel}>
      <h3 className="font-semibold mb-2">Rate Helper Technicians</h3>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Your private ratings of helpers. Helpers never see who rated them.
        Ratings are 1–5 stars and never affect payouts, eligibility, or invoicing.
      </p>
      <ul className="space-y-3">
        {ratable.map((inv) => (
          <LeadRateHelperRow key={inv.id} bookingId={bookingId} invitation={inv} />
        ))}
      </ul>
    </div>
  );
}


function LeadRateHelperRow({ bookingId, invitation }) {
  const [score, setScore] = useState(5);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api(`/leads/me/bookings/${bookingId}/ratings`, {
        method: "POST",
        body: {
          ratings: [{
            helper_user_uuid: invitation.helper_user_uuid,
            score: parseInt(score, 10),
            comment: comment || undefined,
          }],
        },
      });
      toast.success("Rating submitted.");
      setSubmitted(true);
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <li className="border border-[var(--avcp-line)] rounded p-3 bg-[var(--avcp-surface-2)]">
        <div className="text-sm">
          ✓ Rated <strong>{invitation.helper_display_name}</strong> ({score}★)
        </div>
      </li>
    );
  }
  return (
    <li className="border border-[var(--avcp-line)] rounded p-3">
      <div className="text-sm font-medium mb-2">{invitation.helper_display_name}</div>
      <div className="flex items-center gap-2">
        <select className="avcp-input" value={score}
                data-testid={TID.leadRateHelperScore}
                onChange={(e) => setScore(e.target.value)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>
          ))}
        </select>
        <input className="avcp-input flex-1"
                placeholder="Optional comment (helper sees the text, not your identity)"
                data-testid={TID.leadRateHelperComment}
                value={comment} onChange={(e) => setComment(e.target.value)} />
        <button className="avcp-btn-primary text-xs" disabled={submitting}
                data-testid={TID.leadRateHelperSubmit}
                onClick={submit}>{submitting ? "Sending…" : "Submit"}</button>
      </div>
    </li>
  );
}
